import { Component, OnInit, AfterViewInit, ChangeDetectorRef, ViewChild, ElementRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { ApiService } from '../../shared/api.service';
import JSZip from 'jszip';

// Interfaces
interface PhotoItem {
  thumbnail: string;
  original: string;
  loading: boolean;
  progress: number;
}

interface ReviewData {
  rating: number;
  note: string;
  order_id: string;
}

interface PhotoResult {
  url: string;
  thumbnail: string;
  timePhotoboothCetak?: string;
  timePhoto?: string;
}

interface VideoResult {
  url: string;
}

interface GifResult {
  url: string;
}

interface PhotoResults {
  photoboothCetak: PhotoResult[];
  photo: PhotoResult[];
  photoboothVideo?: VideoResult[];
  photoboothGif?: GifResult[];
}

interface OrderData {
  order_id: string;
  kodebooking: string;
  device_id: string;
  code: string;
  phone: string;
  name: string;
  qr_photobooth: string;
  photo_results: PhotoResults;
  is_testing?: string | number;
}

interface DeviceSettings {
  theme_id: string;
  device_settings: {
    data: {
      mode: string;
    };
  };
}

@Component({
  selector: 'app-link-segment-photo',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './link-segment-photo.component.html',
  styleUrl: './link-segment-photo.component.scss'
})
export class LinkSegmentPhotoComponent implements OnInit, AfterViewInit {
  // API Configuration
  private IMAGES_ATLAS = 'https://photobooth.awh.co.id/';

  // Loading state
  isLoading: boolean = true;

  // Background and branding
  bgImage: string = 'https://photobooth.awh.co.id/assets/photobooth/images/bg_hasil.jpg';
  isSelkachi: boolean = false;
  deviceMode: string = 'event'; // 'event' or other

  // Photo data
  linkPhotoCetak: PhotoItem[] = [];
  linkPhotoCetakSatuan: PhotoItem[] = [];
  linkPhotoGif: string[] = [];
  linkPhotoboothVideo: string[] = [];

  // User data
  nama: string = '';
  cleanName: string = '';
  photoExpired: boolean = false;

  // QR Code
  qrCodeData: string = '';
  encodeParam: string = ''; // QR code from route parameter (e.g., 8ca0dn_PP4lRXaryvEnpLQ)

  // Modal states
  showQRModal: boolean = false;
  showPhotoModal: boolean = false;
  showReviewModal: boolean = false;
  showThankYouModal: boolean = false;

  // FAQ state
  activeFaq: number | null = null;

  // Review data
  selectedRating: number = 0;
  reviewNote: string = '';
  orderId: string = '';
  reviewStatus: number = 0; // 0 = not reviewed, 1 = reviewed

  // Scroll container references
  @ViewChild('photoScrollContainer') photoScrollContainer!: ElementRef;
  @ViewChild('gifScrollContainer') gifScrollContainer!: ElementRef;
  @ViewChild('videoScrollContainer') videoScrollContainer!: ElementRef;

  // Swiper instances (will be initialized in ngAfterViewInit)
  photoSwiper: any;
  gifSwiper: any;
  videoSwiper: any;
  photoPreviewSwiper: any;

  constructor(
    private route: ActivatedRoute,
    private apiService: ApiService,
    private sanitizer: DomSanitizer,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    // Get encode parameter from route (e.g., /seg/8ca0dn_PP4lRXaryvEnpLQ)
    this.route.params.subscribe(params => {
      this.encodeParam = params['encode'];
      if (this.encodeParam) {
        this.checkBrowserCompatibility();
        this.loadPhotoData();
      }
    });
  }

  ngAfterViewInit(): void {
    // Initialize Swiper sliders
    // this.initializeSwipers();

    // QR code will be generated after data is loaded in loadPhotoData()
  }

  // Scroll navigation methods
  scrollLeft(type: 'photo' | 'gif' | 'video'): void {
    const container = this.getScrollContainer(type);
    if (container) {
      container.scrollBy({
        left: -320, // Scroll by one item width (300px + 20px gap)
        behavior: 'smooth'
      });
    }
  }

  scrollRight(type: 'photo' | 'gif' | 'video'): void {
    const container = this.getScrollContainer(type);
    if (container) {
      container.scrollBy({
        left: 320, // Scroll by one item width (300px + 20px gap)
        behavior: 'smooth'
      });
    }
  }

  private getScrollContainer(type: 'photo' | 'gif' | 'video'): HTMLElement | null {
    switch (type) {
      case 'photo':
        return this.photoScrollContainer?.nativeElement;
      case 'gif':
        return this.gifScrollContainer?.nativeElement;
      case 'video':
        return this.videoScrollContainer?.nativeElement;
      default:
        return null;
    }
  }

  // Download photo directly when clicked
  downloadPhoto(url: string, filename: string): void {
    console.log('Downloading photo:', filename);

    fetch(url)
      .then(response => response.blob())
      .then(blob => {
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(link.href);
      })
      .catch(error => {
        console.error('Download failed:', error);
        alert('Gagal mendownload foto.');
      });
  }

  // Get QR Code URL for template binding
  getQRCodeUrl(): string {
    // console.log('🔍 getQRCodeUrl called');
    // console.log('qrCodeData value:', this.qrCodeData);

    if (!this.qrCodeData) {
    //   console.warn('⚠️ qrCodeData is empty, returning empty string');
      return '';
    }

    // Using QRServer.com API (more reliable than Google Charts)
    const qrSize = 200;
    const qrData = encodeURIComponent(this.qrCodeData);
    const url = `https://api.qrserver.com/v1/create-qr-code/?size=${qrSize}x${qrSize}&data=${qrData}`;

    // console.log('✅ Generated QR URL:', url);
    return url;
  }

  // QR Image event handlers
  onQRImageLoad(): void {
    // console.log('✅ QR Code image loaded successfully!');
  }

  onQRImageError(event: any): void {
    console.error('❌ QR Code image failed to load:', event);
    console.error('Image src:', event.target?.src);
  }

  // Browser compatibility check
  checkBrowserCompatibility(): void {
    const ua = navigator.userAgent.toLowerCase();
    const isChrome = ua.includes('chrome') && !ua.includes('edg') && !ua.includes('opr');
    const isSafari = ua.includes('safari') && !ua.includes('chrome');

    if (!isChrome && !isSafari) {
      // Show warning for unsupported browsers
      console.warn('For better experience, please use Chrome or Safari');
    }
  }

  // Base64 URL decode
  private base64url_decode(input: string): string {
    // Replace URL-safe characters
    let base64 = input.replace(/-/g, '+').replace(/_/g, '/');

    // Add padding if needed
    const pad = base64.length % 4;
    if (pad) {
      base64 += new Array(5 - pad).join('=');
    }

    // Decode base64
    try {
      // Use standard atob for binary strings
      return atob(base64);
    } catch (e) {
      console.error('Error decoding base64:', e);
      return input;
    }
  }

  // Load photo data from API
  async loadPhotoData(): Promise<void> {
    this.isLoading = true;

    // Decode the encode parameter (matching PHP logic)
    let decodedQr = '';
    try {
      decodedQr = this.base64url_decode(this.encodeParam);
      decodedQr = decodedQr.replace(/\x03/g, '#');
      decodedQr = decodedQr.toUpperCase();
    } catch (e) {
      console.error('Error processing QR string:', e);
    }

    // API endpoint: v2/order/qrstatus/{encode}
    const endpoint = `v2/order/qrstatus/${this.encodeParam}`;

    try {
      const response = await this.apiService.getPhotobooth(endpoint);

    //   console.log('Photo data response:', response);

      if (response?.success && response?.data) {
        const contentPhoto = response.data;

        // Set basic data with safety checks
        this.nama = contentPhoto.name || '';
        this.cleanName = this.nama ? this.nama.replace(/\s/g, '') : '';
        this.orderId = contentPhoto.order_id;
        // Use qr_photobooth from API response, fallback to encodeParam if empty
        this.qrCodeData = contentPhoto.qr_photobooth || this.encodeParam;
        // console.log('QR Code Data:', this.qrCodeData, '(from:', contentPhoto.qr_photobooth ? 'API' : 'encodeParam', ')');

        // Manually trigger change detection
        this.cdr.detectChanges();
        // console.log('🔄 Change detection triggered for QR code');

        // Calculate photo expiration
        this.photoExpired = this.calculatePhotoExpiration(contentPhoto);

        // Get device data
        // await this.getDeviceData(contentPhoto.device_id);

        // Get review status
        // await this.getReviewStatus(contentPhoto.order_id);

        // Process photos
        this.processPhotoData(contentPhoto);

        // Simulate photo loading with progress
        this.simulatePhotoLoading();

        // Show review modal after page loads if photos are not expired
        if (!this.photoExpired) {
          setTimeout(() => {
            this.showReviewModal = true;
          }, 2000); // Show after 2 seconds
        }
      } else {
        alert('Gagal memuat data foto. Silakan coba lagi.');
      }
    } catch (error) {
      console.error('Error loading photo data:', error);
      alert('Gagal memuat data foto. Silakan coba lagi.');
    } finally {
      this.isLoading = false;
    }
  }

  // Get device data
//   private async getDeviceData(deviceId: string): Promise<void> {
//     const endpoint = `device/show_by/device_id/${deviceId}`;

//     try {
//       const response = await this.apiService.getPhotobooth(endpoint);

//       if (response?.success && response?.data?.device && response.data.device[0]) {
//         const device = response.data.device[0];
//         if (device.device_settings && device.device_settings.data) {
//           this.deviceMode = device.device_settings.data.mode;
//         }
//       }
//     } catch (error) {
//       console.error('Error loading device data:', error);
//     }
//   }

  // Get review status
//   private async getReviewStatus(orderId: string): Promise<void> {
//     const endpoint = `reviews/show_by/order_id/${orderId}`;

//     try {
//       const response = await this.apiService.getPhotobooth(endpoint);
//       this.reviewStatus = response?.data === null ? 0 : 1;

//       // Show review modal if not reviewed yet and photos not expired
//       if (this.reviewStatus === 0 && !this.photoExpired) {
//         setTimeout(() => {
//           this.showReviewModal = true;
//         }, 30000); // Show after 30 seconds
//       }
//     } catch (error) {
//       this.reviewStatus = 0;
//     }
//   }

  // Process photo data from API response
  private processPhotoData(contentPhoto: OrderData): void {
    // Process photoboothCetak
    if (contentPhoto.photo_results.photoboothCetak) {
      this.linkPhotoCetak = contentPhoto.photo_results.photoboothCetak.map(photo => ({
        thumbnail: photo.thumbnail,
        original: photo.url,
        loading: true,
        progress: 0
      }));
    }

    // Process photo satuan
    if (contentPhoto.photo_results.photo) {
      this.linkPhotoCetakSatuan = contentPhoto.photo_results.photo.map(photo => ({
        thumbnail: photo.thumbnail,
        original: photo.url,
        loading: true,
        progress: 0
      }));
    }

    // Process photoboothVideo
    if (contentPhoto.photo_results.photoboothVideo) {
      this.linkPhotoboothVideo = contentPhoto.photo_results.photoboothVideo.map(video => {
        // Check if URL is complete (starts with http/https) or relative path
        if (video.url.startsWith('http')) {
          return video.url;
        } else {
          return this.IMAGES_ATLAS + video.url;
        }
      });
    }

    // Process photoboothGif
    if (contentPhoto.photo_results.photoboothGif) {
      this.linkPhotoGif = contentPhoto.photo_results.photoboothGif.map(gif => {
        // Check if URL is complete (starts with http/https) or relative path
        if (gif.url.startsWith('http')) {
          return gif.url;
        } else {
          return this.IMAGES_ATLAS + gif.url;
        }
      });
    }
  }

  // Calculate if photo is expired (7 days)
  private calculatePhotoExpiration(contentPhoto: OrderData): boolean {
    let firstTimePhoto: string | null = null;

    // Find first timePhoto from photoboothCetak
    if (contentPhoto.photo_results.photoboothCetak) {
      for (const photo of contentPhoto.photo_results.photoboothCetak) {
        if (photo.timePhotoboothCetak) {
          firstTimePhoto = photo.timePhotoboothCetak;
          break;
        }
      }
    }

    // If not found in photoboothCetak, search in photo_satuan
    if (!firstTimePhoto && contentPhoto.photo_results.photo) {
      for (const photo of contentPhoto.photo_results.photo) {
        if (photo.timePhoto) {
          firstTimePhoto = photo.timePhoto;
          break;
        }
      }
    }

    // Calculate expiration based on timePhoto
    if (firstTimePhoto) {
      const photoTime = new Date(firstTimePhoto);
      const now = new Date();
      const diffTime = Math.abs(now.getTime() - photoTime.getTime());
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

      if (diffDays >= 7) {
        return true;
      }
    }

    // Override if testing mode
    if (contentPhoto.is_testing && (contentPhoto.is_testing === "1" || contentPhoto.is_testing === 1)) {
      return false;
    }

    return false;
  }

  // Simulate photo loading with progress
  private simulatePhotoLoading(): void {
    const allPhotos = [...this.linkPhotoCetak, ...this.linkPhotoCetakSatuan];

    allPhotos.forEach((photo, index) => {
      const interval = setInterval(() => {
        if (photo.progress < 90) {
          photo.progress += 5;
        }
      }, 200);

      // Load actual image
      const img = new Image();
      img.src = photo.thumbnail;

      img.onload = () => {
        clearInterval(interval);
        photo.progress = 100;
        setTimeout(() => {
          photo.loading = false;
        }, 300);
      };

      img.onerror = () => {
        clearInterval(interval);
        photo.progress = 100;
        photo.loading = false;
      };
    });
  }

  // Photo preview
  openPhotoPreview(index: number, type: 'merge' | 'single'): void {
    this.showPhotoModal = true;
    // TODO: Initialize photo preview swiper with selected index
  }

  closePhotoModal(): void {
    this.showPhotoModal = false;
  }

  downloadCurrentPhoto(): void {
    // TODO: Implement download current photo from swiper
    console.log('Download current photo');
  }

  // QR Code modal
  openQRModal(): void {
    this.showQRModal = true;
  }

  closeQRModal(): void {
    this.showQRModal = false;
  }

  // FAQ accordion
  toggleFaq(index: number): void {
    this.activeFaq = this.activeFaq === index ? null : index;
  }

  // Review modal
  openReviewModal(): void {
    this.showReviewModal = true;
  }

  closeReviewModal(): void {
    this.showReviewModal = false;
  }

  selectRating(rating: number): void {
    this.selectedRating = rating;
  }

  async submitReview(): Promise<void> {
    if (this.selectedRating === 0) {
      alert('Silakan pilih rating.');
      return;
    }

    const reviewData: ReviewData = {
      order_id: this.orderId,
      note: this.reviewNote,
      rating: this.selectedRating
    };

    // Call API to submit review - endpoint: reviews
    const endpoint = `reviews`;

    try {
      const response = await this.apiService.postPhotobooth(endpoint, reviewData);
      console.log('Review submitted successfully:', response);

      if (response?.success) {
        this.reviewStatus = 1;
        this.closeReviewModal();
        this.showThankYouModal = true;
      } else {
        console.error('Review submission failed:', response);
        alert('Gagal mengirim review. Silakan coba lagi.');
      }
    } catch (error) {
      console.error('Error submitting review:', error);
      alert('Gagal mengirim review. Silakan coba lagi.');
    }
  }

  closeThankYouModal(): void {
    this.showThankYouModal = false;
  }

  // Download functions
  downloadAllImages(): void {
    const isApple = /Mac|iPhone|iPod|iPad/.test(navigator.platform);

    if (isApple) {
      this.downloadAsZip();
    } else {
      this.downloadImagesSequentially();
    }
  }

  private async downloadAsZip(): Promise<void> {
    try {
      const zip = new JSZip();
      const folder = zip.folder('photos');

      if (!folder) {
        throw new Error('Failed to create ZIP folder');
      }

      // Collect all URLs to download
      const downloadPromises: Promise<void>[] = [];

      // Add merge photos
      this.linkPhotoCetak.forEach((photo, index) => {
        const promise = fetch(photo.original)
          .then(response => response.blob())
          .then(blob => {
            const filename = `${this.cleanName}_merge_photo_${index + 1}.jpg`;
            folder.file(filename, blob);
          })
          .catch(error => console.error(`Failed to download merge photo ${index + 1}:`, error));
        downloadPromises.push(promise);
      });

      // Add single photos
      this.linkPhotoCetakSatuan.forEach((photo, index) => {
        const promise = fetch(photo.original)
          .then(response => response.blob())
          .then(blob => {
            const filename = `${this.cleanName}_photo_${index + 1}.jpg`;
            folder.file(filename, blob);
          })
          .catch(error => console.error(`Failed to download photo ${index + 1}:`, error));
        downloadPromises.push(promise);
      });

      // Add GIFs
      this.linkPhotoGif.forEach((gifUrl, index) => {
        const promise = fetch(gifUrl)
          .then(response => response.blob())
          .then(blob => {
            const filename = `${this.cleanName}_gif_${index + 1}.mp4`;
            folder.file(filename, blob);
          })
          .catch(error => console.error(`Failed to download GIF ${index + 1}:`, error));
        downloadPromises.push(promise);
      });

      // Wait for all downloads to complete
      await Promise.all(downloadPromises);

      // Generate ZIP file
      const content = await zip.generateAsync({ type: 'blob' });

      // Download ZIP file
      const link = document.createElement('a');
      link.href = URL.createObjectURL(content);
      link.download = `${this.cleanName}_all_moments.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(link.href);

      console.log('✅ All moments downloaded as ZIP');
    } catch (error) {
      console.error('Error creating ZIP:', error);
      alert('Gagal mendownload semua foto. Silakan coba lagi.');
    }
  }

  private async downloadImagesSequentially(): Promise<void> {
    try {
      const allFiles: { url: string; filename: string }[] = [];

      // Collect merge photos
      this.linkPhotoCetak.forEach((photo, index) => {
        allFiles.push({
          url: photo.original,
          filename: `${this.cleanName}_merge_photo_${index + 1}.jpg`
        });
      });

      // Collect single photos
      this.linkPhotoCetakSatuan.forEach((photo, index) => {
        allFiles.push({
          url: photo.original,
          filename: `${this.cleanName}_photo_${index + 1}.jpg`
        });
      });

      // Collect GIFs
      this.linkPhotoGif.forEach((gifUrl, index) => {
        allFiles.push({
          url: gifUrl,
          filename: `${this.cleanName}_gif_${index + 1}.mp4`
        });
      });

      // Download each file with a small delay
      for (let i = 0; i < allFiles.length; i++) {
        const file = allFiles[i];

        try {
          const response = await fetch(file.url);
          const blob = await response.blob();

          const link = document.createElement('a');
          link.href = URL.createObjectURL(blob);
          link.download = file.filename;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(link.href);

          // Add delay between downloads to avoid browser blocking
          if (i < allFiles.length - 1) {
            await new Promise(resolve => setTimeout(resolve, 500));
          }
        } catch (error) {
          console.error(`Failed to download ${file.filename}:`, error);
        }
      }

      console.log('✅ All moments downloaded sequentially');
    } catch (error) {
      console.error('Error downloading images:', error);
      alert('Gagal mendownload semua foto. Silakan coba lagi.');
    }
  }

  downloadVideo(url: string, filename: string, event: Event): void {
    event.preventDefault();

    console.log('Download video:', filename);

    fetch(url)
      .then(response => response.blob())
      .then(blob => {
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(link.href);
      })
      .catch(error => {
        console.error('Download failed:', error);
        alert('Gagal mendownload file.');
      });
  }

  // Helper functions
  isIOS(): boolean {
    return /iPad|iPhone|iPod/.test(navigator.userAgent);
  }
}
